import { MARKETS, type Market } from '@ambernord/shared';
import { Button } from '@ambernord/ui';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader.js';
import { TurnstileWidget } from '../components/TurnstileWidget.js';
import { register } from '../lib/api.js';
import { playAudioCue } from '../lib/audio.js';
import { useTurnstile } from '../lib/useTurnstile.js';

export default function Register() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [legalName, setLegalName] = useState('');
  const [country, setCountry] = useState('');
  const [market, setMarket] = useState<Market>('eu');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const turnstile = useTurnstile();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!turnstile.token) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await register({
        email,
        password,
        legalName,
        country: country.toUpperCase(),
        market,
        turnstileToken: turnstile.token,
      });
      if (!res.ok || !res.data.ok) {
        // Turnstile tokens ir vienreizējs — pēc jebkuras neveiksmes vajag jaunu.
        turnstile.reset();
        setError(
          res.data.error === 'turnstile_failed'
            ? t('turnstile.failed')
            : (res.data.error ?? t('status.error')),
        );
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      setDone(true);
    } catch {
      turnstile.reset();
      setError(t('status.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <AppHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10">
        <section className="rounded-lg border border-slate-200 p-6 dark:border-slate-800">
          <h2 className="mb-4 text-base font-medium">{t('register.heading')}</h2>
          {done ? (
            <div>
              <p className="text-sm text-slate-700 dark:text-slate-300">{t('register.done')}</p>
              <p className="mt-4 text-sm">
                <Link className="text-brand-600 hover:underline dark:text-brand-400" to="/login">
                  {t('register.backToSignIn')}
                </Link>
              </p>
            </div>
          ) : (
            <>
              <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="email">
                    {t('register.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="password">
                    {t('register.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="legalName">
                    {t('register.legalName')}
                  </label>
                  <input
                    id="legalName"
                    type="text"
                    autoComplete="organization"
                    required
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="country">
                    {t('register.country')}
                  </label>
                  <input
                    id="country"
                    type="text"
                    autoComplete="country"
                    required
                    maxLength={2}
                    placeholder="LV"
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm uppercase dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="market">
                    {t('register.market')}
                  </label>
                  <select
                    id="market"
                    value={market}
                    onChange={(e) => setMarket(e.target.value as Market)}
                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                  >
                    {MARKETS.map((m) => (
                      <option key={m} value={m}>
                        {m.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                    {error}
                  </p>
                )}
                <TurnstileWidget turnstile={turnstile} />
                <Button type="submit" disabled={submitting || !turnstile.token}>
                  {submitting ? t('status.loading') : t('register.submit')}
                </Button>
              </form>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                <Link className="text-brand-600 hover:underline dark:text-brand-400" to="/login">
                  {t('register.haveAccount')}
                </Link>
              </p>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
